/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/products/collection
 *
 * Custom & smart collection fields merged fields from:
 * - pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/products/customcollection
 * - pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/products/smartcollection
 *
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCollectionKey,
  ShopifyCollectionToCustomerBuysCollectionKey,
  ShopifyCollectionToCustomerGetsCollectionKey,
  ShopifyCollectionToProductsKey,
  ShopifyCollectionToShopKey,
  ShopifyCollectKey,
  ShopifyCollectToCollectionKey,
  ShopifyCollectToProductKey,
  ShopifyDiscountCustomerBuysCollectionKey,
  ShopifyDiscountCustomerBuysCollectionToCollectionKey,
  ShopifyDiscountCustomerBuysCollectionToDiscountKey,
  ShopifyDiscountCustomerGetsCollectionKey,
  ShopifyDiscountCustomerGetsCollectionToCollectionKey,
  ShopifyDiscountCustomerGetsCollectionToDiscountKey,
  ShopifyDiscountKey,
  ShopifyDiscountToCustomerBuysCollectionKey,
  ShopifyDiscountToCustomerGetsCollectionKey,
  ShopifyProductKey,
  ShopifyProductToCollectionsKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

// A Custom or Smart Collection
const specs: ConnectionModelSpecVersion = {
  key: ShopifyCollectionKey,
  "2022-01": {
    name: "Shopify Collection",
    hasMetafields: true,
    fields: [
      {
        name: "Body",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "body_html" } },
      },
      {
        name: "Handle",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "handle" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Image",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "image" } },
      },
      {
        // The Shopify API doesn't allow querying Smart Collections via `Collects`
        name: "Products",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCollectionToProductsKey,
        configuration: {
          joinModelKey: ShopifyCollectKey,
          relatedModelKey: ShopifyProductKey,
          inverseJoinModelFieldKey: ShopifyCollectToCollectionKey,
          inverseRelatedModelFieldKey: ShopifyCollectToProductKey,
          inverseFieldKey: ShopifyProductToCollectionsKey,
        },
      },
      {
        name: "Published At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "published_at" } },
      },
      {
        name: "Published Scope",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "published_scope" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "Sort Order",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sort_order" } },
      },
      {
        name: "Template Suffix",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "template_suffix" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "title" } },
      },
      {
        name: "Collection Type",
        fieldType: FieldType.Enum,
        // TODO when possible: dominion is Shopify, though we define it
        key: "collection_type",
        configuration: {
          allowMultiple: false,
          options: [{ name: "custom" }, { name: "smart" }],
        },
      },
      {
        // Smart Collection Only
        name: "Disjunctive",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "disjunctive" } },
      },
      {
        // Smart Collection only
        name: "Rules",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "rules" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCollectionToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "rest_list", resource: ["smartCollection", "customCollection"] },
  },
  "2023-07": {
    fields: [
      {
        name: "Customer Gets Through Discounts",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCollectionToCustomerGetsCollectionKey,
        configuration: {
          joinModelKey: ShopifyDiscountCustomerGetsCollectionKey,
          relatedModelKey: ShopifyDiscountKey,
          inverseJoinModelFieldKey: ShopifyDiscountCustomerGetsCollectionToCollectionKey,
          inverseRelatedModelFieldKey: ShopifyDiscountCustomerGetsCollectionToDiscountKey,
          inverseFieldKey: ShopifyDiscountToCustomerGetsCollectionKey,
        },
      },
      {
        name: "Customer Buys Through Discounts",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCollectionToCustomerBuysCollectionKey,
        configuration: {
          joinModelKey: ShopifyDiscountCustomerBuysCollectionKey,
          relatedModelKey: ShopifyDiscountKey,
          inverseJoinModelFieldKey: ShopifyDiscountCustomerBuysCollectionToCollectionKey,
          inverseRelatedModelFieldKey: ShopifyDiscountCustomerBuysCollectionToDiscountKey,
          inverseFieldKey: ShopifyDiscountToCustomerBuysCollectionKey,
        },
      },
    ],
  },
  "2025-04": {
    graphQLTypename: "Collection",
    fields: [
      {
        name: "Body",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "body_html",
            graphQLFieldKey: "descriptionHtml",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Handle",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "handle",
            graphQLFieldKey: "handle",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Image",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "image",
            availableInWebhook: ["collections/create", "collections/update"],
            graphQLFieldKey: "image",
            graphQLSelection: `
          id
          height
          width
          url
          altText
          `,
          },
        },
      },
      {
        name: "Published At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "published_at",
            deprecated: true,
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Published Scope",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "published_scope",
            deprecated: true,
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "updated_at",
            graphQLFieldKey: "updatedAt",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Sort Order",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "sort_order",
            graphQLFieldKey: "sortOrder",
            isShopifyEnum: true,
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [
            { name: "ALPHA_ASC" },
            { name: "ALPHA_DESC" },
            { name: "BEST_SELLING" },
            { name: "CREATED" },
            { name: "CREATED_DESC" },
            { name: "MANUAL" },
            { name: "PRICE_ASC" },
            { name: "PRICE_DESC" },
          ],
        },
      },
      {
        name: "Template Suffix",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "template_suffix",
            graphQLFieldKey: "templateSuffix",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "title",
            graphQLFieldKey: "title",
            availableInWebhook: ["collections/create", "collections/update"],
          },
        },
      },
      // This is a custom field Gadget defines as it is useful for users to know if a Collection is a Smart vs Custom collection, but Shopify doesn't have an explicit field for it
      {
        name: "Collection Type",
        fieldType: FieldType.Enum,
        key: "collection_type",
        contributorConfiguration: {
          [ShopifyDominion.id]: { shopifyFieldKey: "collection_type", graphQLFieldKey: "collectionType", skipGraphQLSelection: true },
        },
        configuration: {
          allowMultiple: false,
          options: [{ name: "custom" }, { name: "smart" }],
        },
      },
      {
        // Smart Collection Only
        name: "Disjunctive",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "disjunctive" } },
        isDeleted: true,
      },
      {
        // Smart Collection only
        name: "Rules",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "rules",
            graphQLFieldKey: "ruleSet",
            graphQLSelection: `
          appliedDisjunctively
          rules {
            column
            condition
            conditionObject
            relation
          }
          `,
          },
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyCollectionToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
      {
        name: "Customer Gets Through Discounts",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCollectionToCustomerGetsCollectionKey,
        configuration: {
          joinModelKey: ShopifyDiscountCustomerGetsCollectionKey,
          relatedModelKey: ShopifyDiscountKey,
          inverseJoinModelFieldKey: ShopifyDiscountCustomerGetsCollectionToCollectionKey,
          inverseRelatedModelFieldKey: ShopifyDiscountCustomerGetsCollectionToDiscountKey,
          inverseFieldKey: ShopifyDiscountToCustomerGetsCollectionKey,
        },
      },
      {
        name: "Customer Buys Through Discounts",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyCollectionToCustomerBuysCollectionKey,
        configuration: {
          joinModelKey: ShopifyDiscountCustomerBuysCollectionKey,
          relatedModelKey: ShopifyDiscountKey,
          inverseJoinModelFieldKey: ShopifyDiscountCustomerBuysCollectionToCollectionKey,
          inverseRelatedModelFieldKey: ShopifyDiscountCustomerBuysCollectionToDiscountKey,
          inverseFieldKey: ShopifyDiscountToCustomerBuysCollectionKey,
        },
      },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "description",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Feedback",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "feedback",
            graphQLSelection: `
            details {
              app {
                id
              }
              feedbackGeneratedAt
              link {
                label
                url
              }
              messages {
                field
                message
              }
              state
            }
            summary
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Has Product",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "description",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Legacy Resource Id",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "legacyResourceId",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Products Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "productsCount",
            graphQLSelection: `
              count
              precision
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "SEO",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "seo",
            graphQLSelection: `
            description
            title
            `,
            graphQLOnlyField: true,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "collections",
      filters: {},
      sortableBy: {
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
        id: { sortKey: "ID", reverse: false },
        relevance: { sortKey: "RELEVANCE", reverse: false },
        title: { sortKey: "TITLE", reverse: false },
      },
    },
  },
};

export const ShopifyCollection = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
